/**
 * The six modes (FS-09 §4.1) and the resolver that turns a mode + a player's unlocked sectors into
 * an ordered `RunSegment` plan. `Game` (FS-09 §4.3) chains segments within one continuous run and
 * loops the plan for `Infinite`/`Zen` — there is exactly one run orchestrator for all six modes
 * (R02).
 */
import { hashSeed } from '../../core/rng';
import { SECTORS } from '../../content/sectors';
import type { AssistId, ModeDef, ModeId } from './types';

export const COUNTDOWN_BARS = 4;
export const SECTOR_TRANSITION_BARS = 4;
export const GAUNTLET_INTERVAL_BARS = 8;
export const ENDLESS_INTENSITY_STEP = 0.12;
export const ENDLESS_INTENSITY_CAP = 1.0;

const ALL_ASSISTS: readonly AssistId[] = ['hitbox', 'speed', 'iframe'];

export const MODES: { readonly [K in ModeId]: ModeDef } = {
  'sector-run': {
    id: 'sector-run',
    displayName: 'Sector Run',
    seedPolicy: { kind: 'fresh' },
    attemptPolicy: { kind: 'unlimited' },
    assistsAllowed: ALL_ASSISTS,
    hullPolicy: 'all-unlocked',
    rules: {
      deathEnabled: true,
      scoreEnabled: true,
      endlessRamp: false,
      bossOnly: false,
      carryAcrossSectors: true,
      practiceSeekAllowed: false,
      chartSource: 'content',
      startingPowerup: null,
    },
    retryAnchor: 'movement-start',
    rankable: true,
  },
  infinite: {
    id: 'infinite',
    displayName: 'Infinite Frequency',
    seedPolicy: { kind: 'fresh' },
    attemptPolicy: { kind: 'unlimited' },
    assistsAllowed: ALL_ASSISTS,
    hullPolicy: 'all-unlocked',
    rules: {
      deathEnabled: true,
      scoreEnabled: true,
      endlessRamp: true,
      bossOnly: false,
      carryAcrossSectors: true,
      practiceSeekAllowed: false,
      chartSource: 'content',
      startingPowerup: null,
    },
    retryAnchor: 'run-start',
    rankable: true,
  },
  daily: {
    id: 'daily',
    displayName: 'Daily Resonance',
    seedPolicy: { kind: 'daily' },
    attemptPolicy: { kind: 'per-utc-day', count: 1 },
    assistsAllowed: [],
    hullPolicy: 'all-unlocked',
    rules: {
      deathEnabled: true,
      scoreEnabled: true,
      endlessRamp: false,
      bossOnly: false,
      carryAcrossSectors: true,
      practiceSeekAllowed: false,
      chartSource: 'content',
      startingPowerup: null,
    },
    retryAnchor: 'none',
    rankable: true,
  },
  gauntlet: {
    id: 'gauntlet',
    displayName: 'Gauntlet',
    seedPolicy: { kind: 'fresh' },
    attemptPolicy: { kind: 'unlimited' },
    assistsAllowed: ALL_ASSISTS,
    hullPolicy: 'all-unlocked',
    rules: {
      deathEnabled: true,
      scoreEnabled: true,
      endlessRamp: false,
      bossOnly: true,
      carryAcrossSectors: true,
      practiceSeekAllowed: false,
      chartSource: 'content',
      startingPowerup: null,
    },
    retryAnchor: 'movement-start',
    rankable: true,
  },
  zen: {
    id: 'zen',
    displayName: 'Zen',
    seedPolicy: { kind: 'fresh' },
    attemptPolicy: { kind: 'unlimited' },
    assistsAllowed: ALL_ASSISTS,
    hullPolicy: 'all-unlocked',
    rules: {
      deathEnabled: false,
      scoreEnabled: false,
      endlessRamp: false,
      bossOnly: false,
      carryAcrossSectors: true,
      practiceSeekAllowed: false,
      chartSource: 'content',
      startingPowerup: null,
    },
    retryAnchor: 'none',
    rankable: false,
  },
  custom: {
    id: 'custom',
    displayName: 'Custom Track',
    seedPolicy: { kind: 'from-chart' },
    attemptPolicy: { kind: 'unlimited' },
    assistsAllowed: ALL_ASSISTS,
    hullPolicy: 'all-unlocked',
    rules: {
      deathEnabled: true,
      scoreEnabled: true,
      endlessRamp: false,
      bossOnly: false,
      carryAcrossSectors: true,
      practiceSeekAllowed: false,
      chartSource: 'runtime-analysis',
      startingPowerup: null,
    },
    retryAnchor: 'run-start',
    rankable: false,
  },
};

export interface RunSegment {
  /** 1-based index into `SECTORS`, 0 for onboarding, or -1 for Custom Track's synthetic segment. */
  readonly sectorIndex: number;
  /** Gauntlet: skip straight to the Finale (boss), no chart hazards before it. */
  readonly bossOnly: boolean;
  /** Hazard-free bars before this segment starts. */
  readonly transitionBars: number;
  /**
   * An authored script drives this segment instead of the live chart (FS-01 R38). Only
   * `'onboarding'` exists; `Game.loadSegment` swaps in the scripted scheduler when it is set.
   */
  readonly scriptId?: 'onboarding';
}

/**
 * FS-01 R14 — onboarding runs exactly once per profile, so it is prepended to whatever plan the
 * player's chosen mode produced rather than being a mode of its own. It carries the run's stats and
 * chains straight into the real content when the script ends (§5.2 bar 45.1).
 */
export const ONBOARDING_SEGMENT: RunSegment = {
  sectorIndex: 0,
  bossOnly: false,
  transitionBars: 0,
  scriptId: 'onboarding',
};

export function withOnboarding(plan: RunSegment[]): RunSegment[] {
  return [ONBOARDING_SEGMENT, ...plan];
}

/**
 * `Daily Resonance`'s content plan is fixed by the UTC date, independent of the player's own
 * unlocks (FS-09 §4.2: "a player who has unlocked only sector 1 plays the same Daily as a player
 * who has unlocked all eight"). There is no backend yet (FS-16) to distribute this — see
 * RESONANCE.md — so the plan is derived locally from the date string, which is stable and
 * identical for every player on a given UTC day regardless of clock skew in *content*, even though
 * the attempt lock itself is only a client-side approximation until FS-16 lands.
 */
export function dailyPlan(utcDate: string): RunSegment[] {
  const h = hashSeed(`daily:${utcDate}`);
  const start = 1 + (h % SECTORS.length);
  const next = 1 + ((start + 2) % SECTORS.length);
  return [
    { sectorIndex: start, bossOnly: false, transitionBars: 0 },
    { sectorIndex: next, bossOnly: false, transitionBars: SECTOR_TRANSITION_BARS },
  ];
}

/** Today's date as `YYYY-MM-DD`, UTC. The client's only source until FS-16 serves it (FS-09 R08). */
export function utcDateString(now: number): string {
  const d = new Date(now);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Resolve a mode + the player's unlocked sectors into an ordered plan (FS-09 §4.3). */
export function resolvePlan(modeId: ModeId, unlockedSectors: readonly number[], utcDate: string): RunSegment[] {
  const sectors = [...unlockedSectors].sort((a, b) => a - b);
  switch (modeId) {
    case 'sector-run':
      return sectors.map((sectorIndex, i) => ({
        sectorIndex,
        bossOnly: false,
        transitionBars: i === 0 ? 0 : SECTOR_TRANSITION_BARS,
      }));
    case 'infinite':
    case 'zen':
      return sectors.map((sectorIndex) => ({ sectorIndex, bossOnly: false, transitionBars: 0 }));
    case 'gauntlet':
      return sectors.map((sectorIndex) => ({ sectorIndex, bossOnly: true, transitionBars: GAUNTLET_INTERVAL_BARS }));
    case 'daily':
      return dailyPlan(utcDate);
    case 'custom':
      return [{ sectorIndex: -1, bossOnly: false, transitionBars: 0 }];
  }
}
